import React, { FunctionComponent, useEffect, useState } from 'react';
import config from 'src/shared/config';
import { T } from 'src/shared/helpers';
import { formatStoreUrl, getSalesChannelUrl } from 'src/shared/salesChannelUrl';
import { useAccountData } from 'src/shared/hooks/useAccountData';
import BellSolid from 'src/shared/svg/bellSolid';
import GlobeEuropeSolid from 'src/shared/svg/globeEuropeSolid';
import StoreAltSolid from 'src/shared/svg/storeAltSolid';
import styled from 'styled-components';
import {
    DropdownContent,
    DropdownItemBody,
    DropdownItemCaption,
    DropdownItemIcon,
    DropdownItemText,
    DropdownList,
    DropdownListItem,
    useDropdown
} from '../dropdown';
import HeadwayBadge from './HeadwayBadge';
import { ZTooltip } from '../ztooltip';
import AccountDropdown from './accountDropdown';
import LanguageDropdown from './languageDropdown';
import SidebarPortalPopup from './SidebarPortalPopup';

const FooterContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    gap: 8px;
`;

const AccountButton = styled.button`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 4px 8px 4px 4px;
    border: none;
    background: transparent;
    cursor: pointer;
    color: #222;
    font-family: inherit;
    font-size: 12px;
    font-weight: 500;
    border-radius: 6px;
    min-width: 0;
    flex: 1 1 auto;
    transition: background 0.15s ease;

    &:hover {
        background: rgba(0, 0, 0, 0.04);
    }
`;

const Avatar = styled.div`
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #222;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
    flex-shrink: 0;
    text-transform: uppercase;
`;

const AccountLabel = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
`;

const CollapsedFooterContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px 0 12px 0;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    flex-shrink: 0;
`;

const CollapsedAccountButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    padding: 0;
    border: none;
    border-radius: 6px;
    background: transparent;
    cursor: pointer;
    font-family: inherit;
    transition: background 0.15s ease;

    &:hover {
        background: rgba(0, 0, 0, 0.05);
    }
`;

const IconGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 2px;
    flex-shrink: 0;
`;

const IconButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border: none;
    background: transparent;
    cursor: pointer;
    color: #555;
    padding: 0;
    border-radius: 6px;
    font-family: inherit;
    position: relative;
    transition:
        background 0.15s ease,
        color 0.15s ease;

    &:hover {
        background: rgba(0, 0, 0, 0.06);
        color: #222;
    }

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }

    svg {
        width: 16px;
        height: 16px;
        fill: currentColor;
    }
`;

const LanguageButton = styled(IconButton)`
    gap: 4px;
    width: auto;
    min-width: 42px;
    padding: 0 6px;
`;

const LanguageCode = styled.span`
    font-family: Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 12px;
    font-weight: 700;
    line-height: 1;
    letter-spacing: 0;
    text-transform: uppercase;
`;

const HeadwayOverlay = styled.div`
    position: absolute;
    top: -2px;
    right: -2px;
    pointer-events: none;

    .headway-badge {
        position: relative;
    }
`;

interface StoreInfo {
    id: string;
    name: string;
    url: string;
}

interface SidebarFooterProps {
    isCollapsed?: boolean;
}

const SidebarFooter: FunctionComponent<SidebarFooterProps> = ({ isCollapsed = false }) => {
    const { data, userInfo } = useAccountData();
    const [isBellOpen, setIsBellOpen] = useState(false);
    const [openUserDropdown, closeUserDropdown, isUserOpen, UserDropdown] = useDropdown();
    const [openLangDropdown, closeLangDropdown, isLangOpen, LangDropdown] = useDropdown();
    const [openStoreDropdown, closeStoreDropdown, isStoreOpen, StoreDropdown] = useDropdown();

    const closeBell = () => {
        setIsBellOpen(false);
        const headway = (window as any).Headway;
        if (headway && typeof headway.hide === 'function') headway.hide();
    };

    useEffect(() => {
        if (!isBellOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                closeBell();
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [isBellOpen]);

    if (!userInfo) return null;

    const name = userInfo.name || '';
    const initial = name.charAt(0) || '?';
    const salesChannels = data?.salesChannels ?? [];
    const languageCode = (config.culture || 'en').split('-')[0].toUpperCase();
    const accountLabel = name || T._('Your Account', 'Admin');

    const rawStores: StoreInfo[] = salesChannels
        .map((channel) => {
            const ecommerceName = channel.ecommerceSettings?.ecommerceName ?? 'NONE';
            const definitiveUrl = channel.ecommerceSettings?.definitiveUrl ?? '';
            const remoteUrl = channel.ecommerceSettings?.remoteUrl ?? '';
            const url = getSalesChannelUrl(ecommerceName, definitiveUrl, remoteUrl);

            return {
                id: channel.id,
                name: channel.detail?.properties?.name?.trim() || formatStoreUrl(url),
                url
            };
        })
        .filter((store) => store.name && store.url);

    const seenUrls = new Set<string>();
    const stores: StoreInfo[] = rawStores.filter((store) => {
        if (seenUrls.has(store.url)) return false;
        seenUrls.add(store.url);
        return true;
    });

    const closeDropdowns = () => {
        closeUserDropdown();
        closeLangDropdown();
        closeStoreDropdown();
    };

    const closeAllPopups = () => {
        closeDropdowns();
        closeBell();
    };

    if (isCollapsed) {
        return (
            <>
                <CollapsedFooterContainer>
                    <ZTooltip position='right' title={accountLabel}>
                        <CollapsedAccountButton
                            type='button'
                            onClick={(e) => {
                                closeBell();
                                isUserOpen ? closeUserDropdown() : openUserDropdown(e.currentTarget, 'right-end');
                            }}
                            aria-label={accountLabel}
                            aria-haspopup='menu'
                            aria-expanded={isUserOpen}
                        >
                            <Avatar>{initial}</Avatar>
                        </CollapsedAccountButton>
                    </ZTooltip>
                </CollapsedFooterContainer>

                {isUserOpen && (
                    <UserDropdown minWidth='330px' maxWidth='330px' surfacePadding='0' zIndex={10000}>
                        <AccountDropdown nameUser={name} isMenuHidden={false} onItemClick={closeUserDropdown} />
                    </UserDropdown>
                )}
            </>
        );
    }

    const handleStoreClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        if (stores.length === 0) return;
        closeAllPopups();

        if (stores.length === 1) {
            window.open(stores[0].url, '_blank', 'noopener,noreferrer');
            return;
        }

        openStoreDropdown(e.currentTarget, 'top-start');
    };

    const openBell = () => {
        closeAllPopups();
        setIsBellOpen(true);
        const headway = (window as any).Headway;
        if (headway && typeof headway.show === 'function') headway.show();
    };

    return (
        <>
            <FooterContainer>
                <AccountButton
                    type='button'
                    onClick={(e) => {
                        closeBell();
                        isUserOpen ? closeUserDropdown() : openUserDropdown(e.currentTarget, 'top-start');
                    }}
                    title={T._('Your Account', 'Admin')}
                    aria-haspopup='menu'
                    aria-expanded={isUserOpen}
                >
                    <Avatar>{initial}</Avatar>
                    <AccountLabel>{accountLabel}</AccountLabel>
                </AccountButton>

                <IconGroup>
                    <IconButton
                        type='button'
                        onClick={handleStoreClick}
                        disabled={stores.length === 0}
                        title={stores.length === 0 ? T._('Connect a store first', 'Admin') : T._('Open your store', 'Admin')}
                        aria-haspopup='menu'
                        aria-expanded={isStoreOpen}
                    >
                        <StoreAltSolid />
                    </IconButton>

                    <IconButton type='button' onClick={openBell} title={T._('Notifications', 'Admin')}>
                        <BellSolid />
                        <HeadwayOverlay>
                            <HeadwayBadge />
                        </HeadwayOverlay>
                    </IconButton>

                    <LanguageButton
                        type='button'
                        onClick={(e) => {
                            closeBell();
                            isLangOpen ? closeLangDropdown() : openLangDropdown(e.currentTarget, 'top-start');
                        }}
                        title={T._('Language', 'Admin')}
                        aria-haspopup='menu'
                        aria-expanded={isLangOpen}
                    >
                        <GlobeEuropeSolid />
                        <LanguageCode>{languageCode}</LanguageCode>
                    </LanguageButton>
                </IconGroup>
            </FooterContainer>

            {isUserOpen && (
                <UserDropdown minWidth='330px' maxWidth='330px' surfacePadding='0' zIndex={10000}>
                    <AccountDropdown nameUser={name} isMenuHidden={false} onItemClick={closeUserDropdown} />
                </UserDropdown>
            )}

            {isLangOpen && (
                <LangDropdown minWidth='250px' maxWidth='250px' maxHeight='400px' surfacePadding='0' zIndex={10000}>
                    <LanguageDropdown onItemClick={closeLangDropdown} />
                </LangDropdown>
            )}

            {isStoreOpen && (
                <StoreDropdown minWidth='280px' maxWidth='320px' surfacePadding='0' zIndex={10000}>
                    <DropdownContent $maxHeight='320px'>
                        <DropdownList>
                            {stores.length > 0 ? (
                                stores.map((store) => (
                                    <DropdownListItem
                                        key={store.id}
                                        as='a'
                                        href={store.url}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        onClick={closeStoreDropdown}
                                    >
                                        <DropdownItemIcon>
                                            <StoreAltSolid />
                                        </DropdownItemIcon>
                                        <DropdownItemBody>
                                            <DropdownItemText>{store.name}</DropdownItemText>
                                            <DropdownItemCaption>{formatStoreUrl(store.url)}</DropdownItemCaption>
                                        </DropdownItemBody>
                                    </DropdownListItem>
                                ))
                            ) : (
                                <DropdownListItem as='div'>
                                    <DropdownItemBody>
                                        <DropdownItemText>{T._('No stores available', 'Admin')}</DropdownItemText>
                                        <DropdownItemCaption>{T._('Connect a channel from Sales channels & Modules', 'Admin')}</DropdownItemCaption>
                                    </DropdownItemBody>
                                </DropdownListItem>
                            )}
                        </DropdownList>
                    </DropdownContent>
                </StoreDropdown>
            )}

            <SidebarPortalPopup open={isBellOpen} onClose={closeBell} />
        </>
    );
};

export default SidebarFooter;
